import type { ResultadoDaIA } from "../lib/leitorDeProcessos";

// O processo de EXEMPLO — "Ver demonstração", que Victor pediu em 02/09/2026.
//
// ---------------------------------------------------------------------------
// Por que ele existe, e por que é marcado em toda tela
// ---------------------------------------------------------------------------
// O valor é pedagógico: o perito entende o que a ferramenta devolve antes de
// gastar uma análise, e antes de subir o processo de um cliente num sistema que
// ele ainda não conhece.
//
// E a doutrina desta casa sobre tela que imita o real pede o inverso na mesma
// medida (o Simulador MEI, `000098`/`000099`): fidelidade visual é o valor, e a
// mesma fidelidade num documento cria um papel que parece verdadeiro. Aqui a
// tela imita um RELATÓRIO sobre um processo — então:
//
//   * o processo NÃO existe, e todo dado dele é inventado. O número segue a
//     forma do CNJ mas com sequencial 0000000 e o zero de verificação, que
//     nenhum processo real usa;
//   * as partes são "EMPRESA EXEMPLO LTDA" e "FULANO DE EXEMPLO" — nomes que
//     não passam por gente;
//   * a tela carrega uma faixa permanente dizendo que é exemplo, e ela NÃO é
//     configurável;
//   * o checklist não grava e o assistente não responde: os dois dizem por quê,
//     em vez de simplesmente não funcionar.
//
// ---------------------------------------------------------------------------
// Por que os dados são CONSTANTE, e não uma análise real guardada
// ---------------------------------------------------------------------------
// Guardar uma análise de verdade como exemplo custaria uma linha em
// `lp_analises` sem dono — e toda RLS daqui é `aluno_id = meu_profile_id()`.
// Uma linha "de todos" seria a primeira exceção àquela regra, aberta para
// sempre, por causa de uma tela de demonstração.
//
// Os dados abaixo são a forma exata que a IA devolve (o mesmo `ResultadoDaIA`
// que a tela desenha), então a demonstração passa pelos MESMOS componentes do
// resultado real. Um exemplo desenhado por uma tela paralela mostraria uma
// ferramenta que não existe.

export const TITULO_DO_EXEMPLO = "Exemplo — reclamatória trabalhista";

/** Quantas páginas o processo de exemplo tem, e quantas a IA leria. */
export const PAGINAS_DO_EXEMPLO = 42;
export const PAGINAS_LIDAS_DO_EXEMPLO = 42;

export const ARQUIVOS_DO_EXEMPLO = [
  { nome: "peticao-inicial.pdf", paginas: 24, caracteres: 41320 },
  { nome: "contestacao.pdf", paginas: 18, caracteres: 29870 },
];

export const RESULTADO_DO_EXEMPLO: ResultadoDaIA = {
  relatorio: {
    identificacao:
      "Processo nº 0000000-00.2024.5.02.0000 — 1ª Vara do Trabalho de São Paulo/SP. " +
      "Reclamante: FULANO DE EXEMPLO. Reclamada: EMPRESA EXEMPLO LTDA. " +
      "Autuado em 15/03/2024 (pág. 1).",
    objetoAcao:
      "Reclamatória trabalhista com pedidos de horas extras excedentes da 8ª diária e 44ª " +
      "semanal no período de 01/2020 a 12/2023, adicional noturno, reflexos em férias, 13º " +
      "salário e FGTS, e indenização por danos morais de R$ 20.000,00. Valor da causa: " +
      "R$ 85.000,00 (págs. 2–3).",
    objetoPericia:
      "Apuração de diferenças de horas extras e adicional noturno no período de 01/2020 a " +
      "12/2023, com base nos cartões de ponto e holerites juntados, conforme decisão " +
      "saneadora de 25/05/2024 (pág. 15).",
    documentosAnalisados: [
      {
        nome: "peticao-inicial.pdf",
        fase: "Petição Inicial",
        classe: "Reclamatória Trabalhista",
        paginas: "1-24",
      },
      {
        nome: "contestacao.pdf",
        fase: "Contestação",
        classe: "Reclamatória Trabalhista",
        paginas: "25-42",
      },
    ],
    pontosControvertidos: [
      {
        texto: "Jornada de trabalho efetivamente cumprida entre 01/2020 e 12/2023 (pág. 5)",
        fonte: "peticao-inicial.pdf",
      },
      {
        texto: "Validade do acordo de compensação de jornada firmado em 05/01/2020 (pág. 28)",
        fonte: "contestacao.pdf",
      },
      {
        texto: "Base de cálculo do adicional noturno e horas alcançadas (pág. 8)",
        fonte: "peticao-inicial.pdf",
      },
      {
        texto: "Existência de trabalho após as 22h, negada pela reclamada (pág. 31)",
        fonte: "contestacao.pdf",
      },
    ],
    quesitos: [
      {
        texto: "Qual a jornada efetivamente cumprida pelo reclamante em cada mês do período?",
        pagina: "pág. 18",
        fonte: "peticao-inicial.pdf",
      },
      {
        texto: "Existem diferenças de horas extras a apurar, e em que montante?",
        pagina: "pág. 18",
        fonte: "peticao-inicial.pdf",
      },
      {
        texto: "O acordo de compensação de jornada foi cumprido no período?",
        pagina: "pág. 19",
        fonte: "peticao-inicial.pdf",
      },
      {
        texto: "Qual o valor do adicional noturno devido, se houver?",
        pagina: "pág. 20",
        fonte: "peticao-inicial.pdf",
      },
    ],
    documentosRelevantes: [
      { nome: "Contrato de trabalho", pagina: "págs. 25–28", fonte: "contestacao.pdf" },
      { nome: "Acordo de compensação de jornada", pagina: "pág. 29", fonte: "contestacao.pdf" },
      { nome: "Cartões de ponto de 07/2020 a 12/2023", pagina: "págs. 30–45", fonte: "contestacao.pdf" },
      { nome: "Holerites de 01/2020 a 12/2023", pagina: "págs. 46–58", fonte: "contestacao.pdf" },
      { nome: "Termo de rescisão do contrato de trabalho", pagina: "pág. 59", fonte: "contestacao.pdf" },
    ],
  },
  linhaDoTempo: [
    {
      data: "15/03/2024",
      evento: "Petição Inicial",
      descricao: "Distribuição da reclamatória trabalhista, com pedido de tutela de urgência.",
      pagina: "pág. 1",
      fonte: "peticao-inicial.pdf",
    },
    {
      data: "10/04/2024",
      evento: "Contestação",
      descricao: "Defesa da reclamada, alegando acordo de compensação válido.",
      pagina: "pág. 25",
      fonte: "contestacao.pdf",
    },
    {
      data: "25/05/2024",
      evento: "Decisão Saneadora",
      descricao: "Determinação de perícia contábil e delimitação do objeto.",
      pagina: "pág. 15",
      fonte: "peticao-inicial.pdf",
    },
    {
      data: "01/06/2024",
      evento: "Nomeação do perito",
      descricao: "Perito nomeado, com prazo de 30 dias para o laudo.",
      pagina: "pág. 16",
      fonte: "peticao-inicial.pdf",
    },
    {
      data: "15/06/2024",
      evento: "Quesitos das partes",
      descricao: "Apresentação dos quesitos pelo reclamante e pela reclamada.",
      pagina: "pág. 18",
      fonte: "peticao-inicial.pdf",
    },
  ],
  alertas: [
    {
      tipo: "Documento Ausente",
      descricao:
        "Não foram localizados os cartões de ponto do período de 01/2020 a 06/2020 — seis " +
        "meses do período controvertido sem controle de jornada nos autos.",
      pagina: "pág. 30",
      fonte: "contestacao.pdf",
    },
    {
      tipo: "Inconsistência de Datas",
      descricao:
        "O holerite de março/2022 apresenta data de competência divergente do período de " +
        "pagamento indicado.",
      pagina: "pág. 50",
      fonte: "contestacao.pdf",
    },
    {
      tipo: "Quesito Genérico",
      descricao:
        "O quesito nº 3 da reclamada não delimita período nem verba, o que dificulta " +
        "resposta objetiva no laudo.",
      pagina: "pág. 21",
      fonte: "contestacao.pdf",
    },
  ],
};

/**
 * O checklist do exemplo. Sem `id` de banco: ele não grava, e a tela diz isso.
 *
 * Dar ids falsos aqui e deixar a caixa clicável seria reproduzir o defeito
 * central da origem de propósito — lá o tique não gravava em lugar nenhum e o
 * contador ficava preso em 0/N.
 */
export const CHECKLIST_DO_EXEMPLO = [
  { texto: "Verificar o objeto delimitado na decisão saneadora", pagina: "pág. 15" },
  { texto: "Conferir os cartões de ponto juntados contra o período controvertido", pagina: "págs. 30–45" },
  { texto: "Levantar a base salarial nos holerites para o cálculo", pagina: "págs. 46–58" },
  { texto: "Analisar a validade do acordo de compensação de jornada", pagina: "pág. 29" },
  { texto: "Identificar as horas trabalhadas após as 22h para o adicional noturno", pagina: "pág. 8" },
  { texto: "Elaborar a memória de cálculo mês a mês", pagina: "a produzir" },
];

/** A resposta que o assistente daria — para a demonstração mostrar a FORMA dela. */
export const CONVERSA_DO_EXEMPLO = [
  {
    papel: "user" as const,
    conteudo: "Quais os valores envolvidos?",
  },
  {
    papel: "assistant" as const,
    conteudo: `## 📋 Resposta
O valor da causa é de "R$ 85.000,00" (pág. 3). O pedido de indenização por danos morais é de "R$ 20.000,00" (pág. 3).

## 📎 Complemento
O período controvertido vai de 01/2020 a 12/2023 (pág. 15). Os holerites juntados cobrem o mesmo período (págs. 46–58), mas os cartões de ponto começam em 07/2020 (págs. 30–45).

## 💡 Explicação
A diferença entre o período dos holerites e o dos cartões de ponto é o primeiro problema do laudo: sem controle de jornada de 01/2020 a 06/2020, a apuração desses seis meses vai depender de prova indireta, e o laudo precisa dizer isso expressamente.

---
*Você também pode perguntar sobre: os quesitos do juízo, o acordo de compensação, os prazos do processo*`,
  },
];
